import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { SyncManager } from '../utils/SyncManager';
import SalesChart from '../components/Dashboard/SalesChart';
import {
    FiDollarSign, FiShoppingBag, FiPackage, FiTrendingUp,
    FiAlertTriangle, FiClock, FiWifiOff
} from 'react-icons/fi';
import './DashboardPage.css';

const DashboardPage = () => {
    const { user, isSuperAdmin } = useAuth();
    const { showToast, confirm, isOffline } = useUI(); // Add isOffline
    const [stats, setStats] = useState(null);
    const [lowStockItems, setLowStockItems] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [sessionStatus, setSessionStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    // Branch Switching for Super Admin
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(user?.branch_id || '');

    useEffect(() => {
        if (isSuperAdmin && !isOffline) {
            fetchBranches();
        }
    }, [isSuperAdmin, isOffline]);

    // Fetch data regardless of offline status (rely on SW Cache)
    useEffect(() => {
        if (isSuperAdmin && selectedBranch) {
            fetchDashboardData();
        } else if (!isSuperAdmin) {
            fetchDashboardData();
        }
    }, [selectedBranch, isSuperAdmin, user]);

    const fetchBranches = async () => {
        try {
            const res = await api.get('/branches');
            setBranches(res.data);
            if (res.data.length > 0 && !selectedBranch) {
                setSelectedBranch(res.data[0].id);
            }
        } catch (error) {
            console.error('Failed to fetch branches:', error);
        }
    };

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const branchQuery = isSuperAdmin ? `?branchId=${selectedBranch}` : '';
            const branchParam = isSuperAdmin ? { params: { branchId: selectedBranch } } : {};

            // Fetch Data
            const businessDate = SyncManager.getBusinessDate ? SyncManager.getBusinessDate() : new Date().toISOString().split('T')[0];
            const [salesRes, alertsRes, ordersRes] = await Promise.all([
                api.get(`/reports/daily-sales?date=${businessDate}${branchQuery ? branchQuery.replace('?', '&') : ''}`),
                api.get('/inventory/alerts', branchParam),
                api.get('/orders?status=completed' + (isSuperAdmin ? `&branchId=${selectedBranch}` : ''))
            ]);

            // Fetch Session Status (Prefer SyncManager if Offline to preserve local actions)
            let sessionData;
            if (isOffline) {
                const cached = await SyncManager.getCachedSession();
                if (cached) {
                    sessionData = cached;
                } else {
                    // Fallback to API/SW Cache if no manual cache
                    try {
                        const res = await api.get('/sessions/current', branchParam);
                        sessionData = res.data;
                    } catch (e) {
                        sessionData = { isOpen: false }; // Default closed
                    }
                }
            } else {
                // Online: fetch fresh
                const res = await api.get('/sessions/current', branchParam);
                sessionData = res.data;
                // Update manual cache
                SyncManager.cacheSession(sessionData);
            }

            setStats({
                todaySales: parseFloat(salesRes.data.total_sales || 0),
                todayOrders: parseInt(salesRes.data.total_orders || 0),
                taxCollected: parseFloat(salesRes.data.total_tax || 0),
            });

            // Merge Offline Stats (Always check pending orders to handle sync delay)
            const pendingOrders = await SyncManager.getPendingOrders();
            if (pendingOrders.length > 0) {
                // Determine which pending orders are NOT already in the API response (deduplicate)
                // Filter out orders that have an offlineId that matches an offline_id in the API response
                const uniquePending = pendingOrders.filter(p =>
                    !ordersRes.data.some(apiOrder => apiOrder.offline_id === p.offlineId)
                );

                if (uniquePending.length > 0) {
                    const pendingSales = uniquePending.reduce((sum, o) => sum + parseFloat(o.totalAmount || 0), 0);
                    const pendingTax = uniquePending.reduce((sum, o) => sum + parseFloat(o.taxAmount || 0), 0);

                    setStats(prev => ({
                        todaySales: prev.todaySales + pendingSales,
                        todayOrders: prev.todayOrders + uniquePending.length,
                        taxCollected: prev.taxCollected + pendingTax
                    }));

                    // Add to recent orders
                    const offlineRecent = uniquePending.map(o => ({
                        id: o.offlineId,
                        order_number: o.orderNumber,
                        created_at: o.createdAt,
                        total_amount: o.totalAmount,
                        status: 'pending-sync' // Visual indicator
                    }));

                    setRecentOrders(prev => {
                        const merged = [...offlineRecent, ...prev];
                        return merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
                    });
                }
            }
            setLowStockItems(alertsRes.data.slice(0, 5));
            setRecentOrders(ordersRes.data.slice(0, 5));
            setSessionStatus(sessionData);

        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            // If offline and no cache, we might still want to show UI (empty)
        } finally {
            setLoading(false);
        }
    };

    const handleOpenShop = async () => {
        const payload = isSuperAdmin ? { branchId: selectedBranch } : {};

        if (isOffline) {
            try {
                await SyncManager.saveOfflineAction({ type: 'OPEN_SHOP', payload });
                const newStatus = {
                    ...sessionStatus,
                    isOpen: true,
                    openedAt: new Date().toISOString(),
                    session: { id: null } // Ensure session object exists for logic
                };
                setSessionStatus(newStatus);
                SyncManager.cacheSession(newStatus); // Update cache logic
                showToast('Shop opened (Saved Offline)', 'success');
            } catch (error) {
                showToast('Failed to save offline action', 'error');
            }
            return;
        }

        try {
            await api.post('/sessions/open', payload);
            fetchDashboardData();
            showToast('Shop opened successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to open shop', 'error');
        }
    };

    const handleCloseShop = async () => {
        const confirmed = await confirm('Are you sure you want to close the shop for today?', 'Close Shop');
        if (!confirmed) return;

        const payload = isSuperAdmin ? { branchId: selectedBranch } : {};

        if (isOffline) {
            try {
                await SyncManager.saveOfflineAction({ type: 'CLOSE_SHOP', payload });
                const newStatus = { ...sessionStatus, isOpen: false, closedAt: new Date().toISOString() };
                setSessionStatus(newStatus);
                SyncManager.cacheSession(newStatus); // Update cache logic
                showToast('Shop closed (Saved Offline)', 'success');
            } catch (error) {
                showToast('Failed to save offline action', 'error');
            }
            return;
        }

        try {
            await api.post('/sessions/close', payload);
            fetchDashboardData();
            showToast('Shop closed successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to close shop', 'error');
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner spinner-lg"></div>
            </div>
        );
    }

    // Render Dashboard (Offline or Online)
    return (
        <div className="dashboard-page">
            <div className="dashboard-header">
                <div>
                    <h1>Dashboard</h1>
                    <p className="text-muted">Welcome back, {user?.firstName}!</p>
                </div>

                <div className="header-actions" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {isSuperAdmin && (
                        <div className="branch-selector">
                            <select
                                className="form-select"
                                value={selectedBranch}
                                onChange={(e) => setSelectedBranch(e.target.value)}
                                style={{ minWidth: '200px' }}
                            >
                                {branches.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Shop Session Control */}
                    <div className="session-control">
                        {sessionStatus?.isOpen ? (
                            <div className="session-open">
                                <div className="session-status">
                                    <span className="status-dot active"></span>
                                    <span>Shop is Open</span>
                                </div>
                                <button className="btn btn-danger btn-sm" onClick={handleCloseShop}>
                                    <FiClock /> Close Shop
                                </button>
                            </div>
                        ) : (
                            <button className="btn btn-success" onClick={handleOpenShop}>
                                <FiClock /> Open Shop
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon sales">
                        <FiDollarSign />
                    </div>
                    <div className="stat-content">
                        <h3>Rs. {parseFloat(stats?.todaySales || 0).toFixed(2)}</h3>
                        <p>Today's Sales</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon orders">
                        <FiShoppingBag />
                    </div>
                    <div className="stat-content">
                        <h3>{stats?.todayOrders || 0}</h3>
                        <p>Today's Orders</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon tax">
                        <FiTrendingUp />
                    </div>
                    <div className="stat-content">
                        <h3>Rs. {parseFloat(stats?.taxCollected || 0).toFixed(2)}</h3>
                        <p>Tax Collected</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon alerts">
                        <FiAlertTriangle />
                    </div>
                    <div className="stat-content">
                        <h3>{lowStockItems.length}</h3>
                        <p>Low Stock Alerts</p>
                    </div>
                </div>
            </div>

            {/* Daily Sales Chart */}
            <SalesChart branchQuery={isSuperAdmin ? `branchId=${selectedBranch}` : ''} />

            <div className="dashboard-content">
                {/* Low Stock Alerts */}
                <div className="card">
                    <div className="card-header">
                        <h3><FiAlertTriangle className="text-warning" /> Low Stock Alerts</h3>
                    </div>
                    <div className="card-body">
                        {lowStockItems.length === 0 ? (
                            <p className="text-muted text-center">No low stock items 🎉</p>
                        ) : (
                            <div className="alert-list">
                                {lowStockItems.map(item => (
                                    <div key={item.id} className="alert-item">
                                        <div className="alert-info">
                                            <h4>{item.name}</h4>
                                            <span className="text-muted">{item.category_name}</span>
                                        </div>
                                        <div className={`stock-badge ${item.quantity === 0 ? 'out' : 'low'}`}>
                                            {item.quantity === 0 ? 'Out of Stock' : `${item.quantity} left`}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Orders */}
                <div className="card">
                    <div className="card-header">
                        <h3><FiShoppingBag /> Recent Orders</h3>
                    </div>
                    <div className="card-body">
                        {recentOrders.length === 0 ? (
                            <p className="text-muted text-center">No orders today</p>
                        ) : (
                            <div className="orders-list">
                                {recentOrders.map(order => (
                                    <div key={order.id} className="order-item">
                                        <div className="order-info">
                                            <h4>{order.order_number}</h4>
                                            <span className="text-muted">
                                                {new Date(order.created_at).toLocaleTimeString()}
                                            </span>
                                        </div>
                                        <div className="order-total">
                                            Rs. {parseFloat(order.total_amount).toFixed(2)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
